import {Directive, HostListener, Input} from '@angular/core';
import {MatFormFieldControl} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';

@Directive({
    selector: '[commonsMaterialInput]',
    providers: [
        {provide: MatFormFieldControl, useExisting: CommonsMaterialInputDirective},
    ],
    // host section has been copied (with (input) exception) from the Angular sources of the corresponding version
    host: {
        'class': 'mat-mdc-input-element',
        '[class.mat-input-server]': '_isServer',
        '[class.mat-mdc-form-field-textarea-control]': '_isInFormField && _isTextarea',
        '[class.mat-mdc-form-field-input-control]': '_isInFormField',
        '[class.mat-mdc-input-disabled-interactive]': 'disabledInteractive',
        '[class.mdc-text-field__input]': '_isInFormField',
        '[class.mat-mdc-native-select-inline]': '_isInlineSelect()',
        '[id]': 'id',
        '[disabled]': 'disabled && !disabledInteractive',
        '[required]': 'required',
        '[attr.name]': 'name || null',
        '[attr.readonly]': '_getReadonlyAttribute()',
        '[attr.aria-disabled]': 'disabled && disabledInteractive ? "true" : null',
        '[attr.aria-invalid]': '(empty && required) ? null : errorState',
        '[attr.aria-required]': 'required',
        '[attr.id]': 'id',
        '(focus)': 'focus()',
        '(blur)': 'blur()',
    },
})
export class CommonsMaterialInputDirective extends MatInput {
    @Input() maxInputLength = 255;
    @Input() allowedChars: RegExp;

    focused = false;

    @HostListener('input', ['$event.target'])
    input(target: HTMLInputElement): void {
        const formattedValue = this.formatValue(target.value, target.selectionStart);
        if (target.value !== formattedValue.value) {
            this.setInputValue(formattedValue.value);
            target.selectionStart = formattedValue.caretPosition;
            target.selectionEnd = formattedValue.caretPosition;
        }
    }

    blur(): void {
        this.focusChanged(false);
        const value = this.value;
        if (typeof value === 'string' && value !== value.trim()) {
            this.setInputValue(value.trim());
        }
    }

    focus(): void {
        this.focusChanged(true);
    }

    protected focusChanged(isFocused: boolean): void {
        if (isFocused !== this.focused && !this.disabled) {
            this.focused = isFocused;
            this.stateChanges.next();
        }
    }

    protected formatValue(value: string, caretPosition: number): FormattedValue {
        if (value.length > this.maxInputLength) {
            value = value.slice(0, this.maxInputLength);
        }
        return this.cutUntilSymbolsCheckSucceed(value, caretPosition);
    }

    private setInputValue(value: string): void {
        this.ngControl.control.setValue(value);
    }

    private cutUntilSymbolsCheckSucceed(value: string, caretPosition: number): FormattedValue {
        if (value.length === 0 || !this.allowedChars || this.allowedChars.test(value)) {
            return {value: value || null, caretPosition};
        }
        const newValue = value.slice(0, caretPosition - 1) + value.slice(caretPosition);
        return this.cutUntilSymbolsCheckSucceed(newValue, caretPosition - 1);
    }
}

interface FormattedValue {
    value: string;
    caretPosition: number;
}
